import { queryOptions } from "@tanstack/react-query";

import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";
import {
  type DcmpBillingSnapshotParams,
  type DcmpBillingSnapshotResponse,
  isDcmpBillingSnapshotResponse,
} from "./dcmpBillingTypes";

export const dcmpBillingQueryKeys = {
  all: [...operatorsQueryKeys.all, "dashboards", "dcmp-billing"] as const,
  snapshot: (params: DcmpBillingSnapshotParams) =>
    [...dcmpBillingQueryKeys.all, "snapshot", params] as const,
};

function throwIfUnauthorized(response: Response): void {
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
}

async function throwIfForbidden(response: Response, fallbackMessage: string): Promise<void> {
  if (response.status !== 403) {
    return;
  }

  const message = await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage);
  throw new OperatorsForbiddenError(message || fallbackMessage);
}

export async function fetchDcmpBillingSnapshot(
  params: DcmpBillingSnapshotParams = {},
): Promise<DcmpBillingSnapshotResponse> {
  const search = new URLSearchParams({
    clinic: params.clinic || "all",
  });
  if (params.month) {
    search.set("month", params.month);
  }

  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/dcmp-billing/snapshot?${search}`,
    { credentials: "include" },
  );
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load DCMP Billing snapshot",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!isDcmpBillingSnapshotResponse(body)) {
    throw new Error("DCMP Billing snapshot response was malformed");
  }
  return body;
}

export function dcmpBillingSnapshotQueryOptions(params: DcmpBillingSnapshotParams = {}) {
  return queryOptions({
    queryKey: dcmpBillingQueryKeys.snapshot(params),
    queryFn: () => fetchDcmpBillingSnapshot(params),
  });
}
